import { dateOnlyFormat } from '../../util/dateUtil';
import { callApi } from '../../util/apiWrapper';
import { logException, appInsights } from '../../services/appInsights';
import { logAudit } from '../../util/auditUtil';
import { updateAwardTotals, getScholarshipNameFromId } from '../../util/scholarshipsUtil';

// This page displays status completely different than almost all other pages, so it doesn't use miscUtil.
export const displayStatus = (status) => {
  if (!status) return '';
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === 'qualified' || lowerStatus === 'qualifiedinitial' || lowerStatus === 'qualifiedprogram') {
    return 'READY FOR COMMITTEE REVIEW';
  }
  if (lowerStatus === 'underreview') {
    return 'UNDER REVIEW';
  }
  if (lowerStatus === 'reopened') {
    return 'RE-OPENED';
  }
  return status.toUpperCase();
};

export const createYearSelectHandler = (yearsList, store, setAcademicYear, fetchRecords, setPageOptions) => {
  return (year) => {
    // #STRY0052444 If previous year, show all, otherwise do not.
    if (year !== yearsList[yearsList.length - 2].label) {
      store.setShowCompletedScholarshipApps(true);
    } else {
      store.setShowCompletedScholarshipApps(false);
    }
    setAcademicYear(year);
    fetchRecords(year);
    // Force back to page 1
    setPageOptions({
      searching: false,
      page: 0,
      perPage: 15,
    });
  };
};

export const getBeginYear = (year) => {
  const yeararray = year.split(' - ');
  const begin = yeararray[0] + '-08-01';
  return begin;
};

export const getEndYear = (year) => {
  const yeararray = year.split(' - ');
  const end = yeararray[1] + '-07-31';
  return end;
};

export const filterData = (val, setFilter) => {
  setFilter(val);
};

export const isShowAllDisabled = (yearsList, academicYear) => {
  if (yearsList && yearsList.length > 0) {
    if (academicYear !== yearsList[yearsList.length - 2]?.label) {
      return true;
    }
  }
  return false;
};

export const filterForAcademicYear = (year, data) => {
  if (!year) return [];
  if (!data || data.length === 0) return [];
  const begin = dateOnlyFormat(getBeginYear(year));
  const end = dateOnlyFormat(getEndYear(year));
  if (!begin || !end || !data || data.length === 0) return [];
  const first = data.filter((a) => dateOnlyFormat(a.awarded_on) >= begin);
  const second = first.filter((a) => dateOnlyFormat(a.awarded_on) <= end);
  return second;
};

export const fetchScholarshipAwards = async (id, auth, store, setAppAwardsFetched, setErrorMsg) => {
  try {
    const list = await callApi(auth, `awards/app?scholarship_application_id=${id}`, 'GET');
    store.setAppAwards(list.result);
    setAppAwardsFetched(true);
  } catch (error) {
    logException(error);
    setErrorMsg('An unexpected error has occurred. Please try again.');
    console.log('Error fetching list of scholarship awards by application:', error);
  }
};

export const fetchAllScholarshipAwards = async (auth, store, setErrorMsg) => {
  try {
    const list = await callApi(auth, `awards`, 'GET');
    store.setAllAwards(list.result);
  } catch (error) {
    logException(error);
    setErrorMsg('An unexpected error has occurred. Please try again.');
    console.log('Error fetching list of all scholarship awards:', error);
  }
};

export const fetchPlanningForm1 = async (userId, auth, store, setSeniorPlanning1, setErrorMsg) => {
  try {
    const pf1 = await callApi(auth, 'planningform1/' + userId, 'GET');
    const score = pf1.result;
    setSeniorPlanning1(score);
    store.setPlanningForm1Data(score);
  } catch (error) {
    logException(error);
    setErrorMsg('An unexpected error has occurred. Please try again.');
    console.log('Error fetching planning form 1 data:', error);
  }
};

export const fetchPlanningForm2 = async (userId, auth, store, setSeniorPlanning2, setErrorMsg) => {
  try {
    const pf2 = await callApi(auth, 'planningform2/' + userId, 'GET');
    const score = pf2.result;
    setSeniorPlanning2(score);
    store.setPlanningForm2Data(score);
  } catch (error) {
    logException(error);
    setErrorMsg('An unexpected error has occurred. Please try again.');
    console.log('Error fetching planning form 2 data:', error);
  }
};

export const handleCardSortChange = (event, pageState, setPageState) => {
  setPageState({ ...pageState, sortBy: event.target.value });
};

export const handleOpenAwardsDialog = async (
  event,
  record,
  id,
  type,
  auth,
  store,
  pageState,
  setPageState,
  setAppClicked,
  setOriginalStatus,
  setAppAwardsFetched,
  setErrorMsg,
  setAppSenior,
  setSeniorPlanning1,
  setSeniorPlanning2
) => {
  event.preventDefault();
  event.stopPropagation();
  setAppClicked(record);
  setOriginalStatus(record.status);
  fetchScholarshipAwards(id, auth, store, setAppAwardsFetched, setErrorMsg);

  store.setScholarshipAppId(id);
  if (type === 'alumni') {
    store.setAlumni(true);
  } else {
    store.setAlumni(false);
  }

  try {
    setPageState({ ...pageState, loading: true });
    let senior = await callApi(auth, 'studentplan/' + record.userId, 'GET');
    setAppSenior(senior.result);
    if (senior.result) {
      fetchPlanningForm1(senior.result.id, auth, store, setSeniorPlanning1, setErrorMsg);
      fetchPlanningForm2(senior.result.id, auth, store, setSeniorPlanning2, setErrorMsg);
    }
    setPageState({ ...pageState, loading: false });
  } catch (error) {
    logException(error);
    setPageState({ ...pageState, loading: false });
    console.log('Error fetching studentplan for scholarship app:', error);
  }
};

export const handleShowAll = (store, fetchRecords, academicYear) => {
  store.setShowCompletedScholarshipApps(!store.showCompletedScholarshipApps);
  fetchRecords(academicYear);
};

export const closeAwardsDialog = (setShowAwardsDialog) => {
  setShowAwardsDialog(false);
};

export const handleOpenScholarshipApp = async (event, id, type, auth, store, pageState, setPageState, history) => {
  event.preventDefault();
  event.stopPropagation();
  store.setScholarshipAppId(id);
  if (type === 'alumni') {
    store.setAlumni(true);
  } else {
    store.setAlumni(false);
  }
  try {
    setPageState({ ...pageState, loading: true });
    let app = await callApi(auth, 'scholarship/' + id, 'GET');
    if (app && app.result) {
      store.setScholarshipApp(app.result);
      store.setScholarshipAppId(app.result.id);
      const appUserId = app.result.userId;
      let senior = await callApi(auth, 'studentplan/' + appUserId, 'GET');
      store.setSenior(senior.result);
    }
    setPageState({ ...pageState, loading: false });
    history.push('/scholarshipapp');
  } catch (error) {
    logException(error);
    setPageState({ ...pageState, loading: false });
    console.log('Error fetching senior:', error);
  }
};

export const updateAwardAmount = (rec, amount, store) => {
  // if an award is being updated, update the store.appAwards record using the id
  if (rec.id > 0) {
    const ind = store.appAwards.findIndex((obj) => obj.id === rec.id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], amount };
      store.setAppAwards(updatedAwards);
    }
  } else {
    // if a new award is being added, update the store.appAwards record using the tempid
    const ind = store.appAwards.findIndex((obj) => obj.tempid === rec.tempid && obj.scholarship_application_id === rec.scholarship_application_id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], amount };
      store.setAppAwards(updatedAwards);
    }
  }
};

export const sendApplicationReturnedEmail = async (id, auth) => {
  try {
    await callApi(auth, 'scholarshipreturnemail', 'POST', { student_id: id });
  } catch (error) {
    logException(error);
    console.log('Error sending scholarship returned email:', error);
  }
};

export const deleteAward = async (record, auth, store, pageUserId, pageState, setPageState, fetchAllScholarshipAwards) => {
  try {
    setPageState({ ...pageState, loading: true });

    if (record.id && record.id > 0) {
      await callApi(auth, 'awards/' + record.id, 'DELETE');

      appInsights.trackTrace({ message: `Scholarship award ${record.id} deleted by ${auth.user.result.email}` });
      const auditObj = {
        audit_ts: new Date(),
        user_id: pageUserId,
        username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
        audit_type: 'scholarship award',
        action: 'deleted',
        value: 'Award ' + JSON.stringify(record) + ' deleted.',
      };
      logAudit(auth, auditObj);

      // Remove award from app awards list and all awards list.
      const idx = store.appAwards.findIndex((s) => s.id === record.id);
      if (idx > -1) {
        const updatedAppAwards = [...store.appAwards];
        updatedAppAwards.splice(idx, 1);
        store.setAppAwards(updatedAppAwards);
      }
      const idx3 = store.allAwards.findIndex((s) => s.id === record.id);
      if (idx3 > -1) {
        const updatedAllAwards = [...store.allAwards];
        updatedAllAwards.splice(idx3, 1);
        store.setAllAwards(updatedAllAwards);
      }

      updateAwardTotals(store.appAwards); // Update totals for this application
      fetchAllScholarshipAwards(); // update totals for all
    } else {
      const ind = store.appAwards.findIndex((s) => s.tempid === record.tempid);
      if (ind > -1) {
        const updatedAppAwards = [...store.appAwards];
        updatedAppAwards.splice(ind, 1);
        store.setAppAwards(updatedAppAwards);
      }
    }
    setPageState({ ...pageState, loading: false });
  } catch (error) {
    logException(error);
    setPageState({ ...pageState, loading: false });
    console.log('Error deleting scholarship award:', error);
  }
};

export const createAward = async (award, auth, store, appSenior, pageUserId, pageState, setPageState, fetchAllScholarshipAwards) => {
  try {
    setPageState({ ...pageState, loading: true });

    let scholarshipName = '';
    let version = 0;
    const ind = store.scholarships.findIndex((obj) => obj.id === award.scholarship_id);
    if (ind > -1) {
      scholarshipName = store.scholarships[ind].name;
      version = store.scholarships[ind].version;
    }

    let item = {
      scholarship_application_id: award.scholarship_application_id,
      userId: appSenior.userId,
      scholarship_id: award.scholarship_id,
      version: version,
      name: scholarshipName,
      status: award.status || 'pending',
      amount: award.amount,
      notes: award.notes,
      awarded_on: award.awarded_on ? award.awarded_on : award.status === 'awarded' ? new Date() : null,
      'updated_at': new Date(),
      'updated_by': pageUserId,
    };

    const app = await callApi(auth, 'awards', 'POST', { item });

    // Updated award record in awards lists
    const updatedAppAwards = [...store.appAwards, app.result];
    const updatedAllAwards = [...store.allAwards, app.result];
    store.setAppAwards(updatedAppAwards);
    store.setAllAwards(updatedAllAwards);

    appInsights.trackTrace({ message: `Scholarship award created by ${auth.user.result.email}` });
    const auditObj = {
      audit_ts: new Date(),
      user_id: pageUserId,
      username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
      audit_type: 'scholarship award',
      action: 'created',
      value: JSON.stringify(item).substring(0, 999),
    };
    logAudit(auth, auditObj);
    setPageState({ ...pageState, loading: false });

    updateAwardTotals(store.appAwards); // Update totals for this application
    fetchAllScholarshipAwards(); // update totals for all
  } catch (error) {
    logException(error);
    setPageState({ ...pageState, loading: false });
    console.log('Error creating scholarship award:', error);
  }
};

export const updateAward = async (
  award,
  auth,
  store,
  pageUserId,
  pageState,
  setPageState,
  setAwardPendingTotal,
  setAwardAwardedTotal,
  setAwardTotal,
  fetchAllScholarshipAwards,
  setErrorMsg
) => {
  try {
    setPageState({ ...pageState, loading: true });

    let scholarshipName = '';
    let version = 0;
    const ind = store.scholarships.findIndex((obj) => obj.id === award.scholarship_id);

    if (ind > -1) {
      scholarshipName = store.scholarships[ind].name;
      version = store.scholarships[ind].version;
    }

    let item = {
      id: award.id,
      scholarship_application_id: award.scholarship_application_id,
      userId: award.userId,
      scholarship_id: award.scholarship_id,
      version: version,
      name: scholarshipName,
      status: award.status,
      amount: award.amount,
      notes: award.notes,
      awarded_on: award.status === 'pending' ? null : award.awarded_on,
      updated_at: new Date(),
      updated_by: pageUserId,
    };

    const res = await callApi(auth, 'awards', 'PUT', { item });

    // Updated award record in awards lists
    if (res && res.result) {
      const updatedAward = res.result;
      const appAwardIndex = store.appAwards.findIndex((s) => s.id === updatedAward.id);
      if (appAwardIndex > -1) {
        const updatedAppAwards = [...store.appAwards];
        updatedAppAwards[appAwardIndex] = updatedAward;
        store.setAppAwards(updatedAppAwards);
      }

      const allAwardIndex = store.allAwards.findIndex((s) => s.id === updatedAward.id);
      if (allAwardIndex > -1) {
        const updatedAllAwards = [...store.allAwards];
        updatedAllAwards[allAwardIndex] = updatedAward;
        store.setAllAwards(updatedAllAwards);

        // Recalculate award totals for the application in the list view
        if (pageState.records && pageState.records.length > 0) {
          const appRecord = pageState.records.find((r) => r.id === award.scholarship_application_id);
          if (appRecord) {
            let pendingAwards = 0;
            let awardedAwards = 0;
            let awardedAmount = 0;

            updatedAllAwards.forEach((a) => {
              if (a.scholarship_application_id === appRecord.id) {
                if (a.status === 'pending') {
                  pendingAwards++;
                } else if (a.status === 'awarded') {
                  awardedAwards++;
                  awardedAmount += parseInt(a.amount || 0, 10);
                }
              }
            });

            appRecord.pendingAwards = pendingAwards;
            appRecord.awardedAwards = awardedAwards;
            appRecord.awardedAmount = awardedAmount;

            setPageState({ ...pageState, records: [...pageState.records] });
          }
        }
      }
    }

    appInsights.trackTrace({ message: `Scholarship award updated by ${auth.user.result.email}` });
    const auditObj = {
      audit_ts: new Date(),
      user_id: pageUserId,
      username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
      audit_type: 'scholarship award',
      action: 'updated',
      value: JSON.stringify(item).substring(0, 999),
    };
    logAudit(auth, auditObj);
    setPageState({ ...pageState, loading: false });

    const totalResults = updateAwardTotals(store.appAwards);
    setAwardPendingTotal(totalResults.pendingTotal);
    setAwardAwardedTotal(totalResults.awardedTotal);
    setAwardTotal(totalResults.total);

    fetchAllScholarshipAwards(auth, store, setErrorMsg);
  } catch (error) {
    logException(error);
    setPageState({ ...pageState, loading: false });
    console.log('Error writing updated scholarship award:', error);
  }
};

export const updateApplicationStatus = async (id, status, auth, store, pageState, setPageState, originalStatus, setAppClicked, appClicked) => {
  try {
    setPageState({ ...pageState, loading: true });

    const fetchIndividualApp = await callApi(auth, `scholarship/${id}`, 'GET');
    const app = fetchIndividualApp.result;

    // Send email
    if (originalStatus !== 'returned' && status === 'returned') {
      await sendApplicationReturnedEmail(app.userId, auth);
    }

    const item = {
      id: app.id,
      userId: app.userId,
      application: JSON.parse(app.application),
      status,
      submitted_date: app.submitted_date,
      disposition_reason: app.disposition_reason,
      youth_strengths: app.youth_strengths,
      ssm_model_category: app.ssm_model_category,
      application_score: app.application_score,
      essay_score: app.essay_score,
      spirituality_score: app.spirituality_score,
      pell_amount: app.pell_amount,
      planning_meeting_1_score: app.planning_meeting_1_score,
      planning_meeting_2_score: app.planning_meeting_2_score,
      gpa_score: app.gpa_score,
      financial_need_score: app.financial_need_score,
      length_of_stay_score: app.length_of_stay_score,
      essay_voice_score: app.essay_voice_score,
      essay_inspiration_score: app.essay_inspiration_score,
      essay_word_score: app.essay_word_score,
      essay_completed_score: app.essay_completed_score,
      citizenship_letters_score: app.citizenship_letters_score,
      citizenship_activities_score: app.citizenship_activities_score,
      citizenship_rolemodel_score: app.citizenship_rolemodel_score,
      status_date: new Date(),
      applicant_type: app.applicant_type,
      status_by: auth.user.result.first_name + ' ' + auth.user.result.last_name,
    };

    const res = await callApi(auth, 'scholarship', 'PUT', { item });

    // Updated status in page table list
    if (res.result) {
      setAppClicked({ ...appClicked, status });
      store.setScholarshipApp(res.result);
      store.setScholarshipAppId(res.result.id);
      let tempApp = pageState.records.find((s) => s.id === app.id);
      if (tempApp) {
        tempApp.status = res.result.status;
      }
    }

    setPageState({ ...pageState, loading: false });
  } catch (error) {
    logException(error);
    setPageState({ ...pageState, loading: false });
    console.log('Error updating scholarship application status:', error);
  }
};

export const updateAwardScholarship = (rec, scholarship_id, store) => {
  const selectedName = getScholarshipNameFromId(scholarship_id, store.scholarships);

  // if an existing award is being updated, update corresponding store.appAwards record with new scholarship_id.
  if (rec.id > 0) {
    const ind = store.appAwards.findIndex((obj) => obj.id === rec.id);

    if (ind > -1) {
      // update existing record with new scholarship_id and clear out name (it will be updated on save)
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], scholarship_id, name: selectedName };
      store.setAppAwards(updatedAwards);
    }
  } else {
    // if a new award is being added, update the store.appAwards record using the tempid
    const ind = store.appAwards.findIndex((obj) => obj.tempid === rec.tempid && obj.scholarship_application_id === rec.scholarship_application_id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], scholarship_id, name: selectedName };
      store.setAppAwards(updatedAwards);
    }
  }
};

export const updateAwardStatus = (rec, status, store) => {
  if (rec.id > 0) {
    const ind = store.appAwards.findIndex((obj) => obj.id === rec.id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      const awarded_on = status === 'awarded' || status === 'denied' ? new Date() : updatedAwards[ind].awarded_on;
      updatedAwards[ind] = { ...updatedAwards[ind], status, awarded_on };
      store.setAppAwards(updatedAwards);
    }
  } else {
    // if a new award is being added, update the store.appAwards record using the tempid
    const ind = store.appAwards.findIndex((obj) => obj.tempid === rec.tempid && obj.scholarship_application_id === rec.scholarship_application_id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      const awarded_on = status === 'awarded' || status === 'denied' ? new Date() : updatedAwards[ind].awarded_on;
      updatedAwards[ind] = { ...updatedAwards[ind], status, awarded_on };
      store.setAppAwards(updatedAwards);
    }
  }
};
export const updateAwardedOn = (rec, awardedDate, store) => {
  // if an award is being updated, update the store.appAwards record using the id
  if (rec.id > 0) {
    const ind = store.appAwards.findIndex((obj) => obj.id === rec.id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], awarded_on: awardedDate };
      store.setAppAwards(updatedAwards);
    }
  } else {
    // if a new award is being added, update the store.appAwards record using the tempid
    const ind = store.appAwards.findIndex((obj) => obj.tempid === rec.tempid && obj.scholarship_application_id === rec.scholarship_application_id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], awarded_on: awardedDate };
      store.setAppAwards(updatedAwards);
    }
  }
};

export const updateAwardNotes = (rec, notes, store) => {
  // if an award is being updated, update the store.appAwards record using the id
  if (rec.id > 0) {
    const ind = store.appAwards.findIndex((obj) => obj.id === rec.id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], notes };
      store.setAppAwards(updatedAwards);
    }
  } else {
    // if a new award is being added, update the store.appAwards record using the tempid
    const ind = store.appAwards.findIndex((obj) => obj.tempid === rec.tempid && obj.scholarship_application_id === rec.scholarship_application_id);
    if (ind > -1) {
      const updatedAwards = [...store.appAwards];
      updatedAwards[ind] = { ...updatedAwards[ind], notes };
      store.setAppAwards(updatedAwards);
    }
  }
};
